import './FormikCheckboxListField.css';
import type { StringKey } from '@ipfabric/utils';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import classNames from 'classnames';
import type { FormikProps } from 'formik';
import type { JSX, ReactNode } from 'react';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

interface Props<TFormikValues, TOptionType> {
  disabled?: boolean;
  formik: FormikProps<TFormikValues>;
  name: StringKey<TFormikValues> | NestedFormikName;
  options: TOptionType[];
  optionClassName?: string;
}

export interface OptionType {
  label: ReactNode;
  value: unknown;
}

export const FormikCheckboxListField = <
  TFormikValues,
  TOptionType extends OptionType,
>(
  props: Props<TFormikValues, TOptionType>,
): JSX.Element => {
  const field = props.formik.getFieldProps<Set<TOptionType['value']>>(
    props.name,
  );

  const toggleItem = (clickedValue: TOptionType['value']) => () => {
    if (props.disabled) {
      return;
    }
    props.formik.setFieldTouched(props.name, true);
    field.onChange({
      target: {
        name: props.name,
        value: plainSet.toggle(clickedValue)(field.value),
      },
    });
  };

  return (
    <>
      {props.options.map((option, index) => (
        <div
          className={classNames(
            props.optionClassName,
            'formik-checkbox-list-field__item py-lgu-6 flex cursor-pointer flex-row px-5',
          )}
          key={
            typeof option.value === 'string' || typeof option.value === 'number'
              ? `s/${option.value}`
              : index
          }
          onClick={toggleItem(option.value)}
        >
          <span className='flex-1'>{option.label}</span>
          <input
            checked={field.value.has(option.value)}
            className='pointer-events-none'
            disabled={props.disabled}
            readOnly
            type='checkbox'
          />
        </div>
      ))}
    </>
  );
};
